// Adds a theme-native carousel-nav footer (page counter + prev/next arrows) to
// EVERY Moast (#Aristopeters) UGC carousel on the page, matching the native
// <carousel-nav> 1:1 — so it works wherever the Moast section is placed (home,
// PDP, anywhere) without editing each template.
//
// Moast renders <moast-carousel> as a Lit + Embla web component using Shadow DOM,
// so the theme cannot style or reach its internal controls. We inject our own
// footer into the light DOM (cloned from the #moast-carousel-nav-template rendered
// once in layout/theme.liquid) right after each Moast app block, and drive the
// carousel through the component it exposes.
//
// IMPORTANT:
// - In "cards" mode the component OWNS the scroll position — Embla's
//   scrollNext()/scrollPrev() are no-ops. Navigation uses the component methods
//   `_scrollToNext()` / `_scrollToPrevious()`; the Embla instance at `._carousel`
//   is used only to READ state and to listen for the 'select' event.
// - Native controls live in the (open) Shadow DOM; the app's `custom_css` setting
//   does not reliably reach it, so we inject a <style> into the shadow root.
// - The footer only shows when the carousel is scrollable; if every slide fits it
//   hides itself, like the native carousels.
// - These are the app's internals — best-effort: if the component never hydrates,
//   the footer stays hidden so the page never shows dead controls.

const TEMPLATE_ID = 'moast-carousel-nav-template';

class MoastCarouselNav extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.moast = (this.section || document).querySelector('moast-carousel');
    this.prevButton = this.querySelector('[data-direction="previous"]');
    this.nextButton = this.querySelector('[data-direction="next"]');
    this.counter = this.querySelector('.carousel-nav__counter');

    // Hidden until we confirm the carousel exists AND is scrollable.
    this.hidden = true;

    if (!this.moast) return;

    this.handlePrev = () => this.#navigate('previous');
    this.handleNext = () => this.#navigate('next');
    this.update = this.update.bind(this);
    // Debounced: Moast re-initializes its Embla instance on resize, so wait for
    // it to settle before re-reading scroll state / re-subscribing.
    this.handleResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.#sync(), 250);
    };

    this.prevButton.addEventListener('click', this.handlePrev);
    this.nextButton.addEventListener('click', this.handleNext);
    window.addEventListener('resize', this.handleResize);

    this.#whenReady();
  }

  disconnectedCallback() {
    if (this.pollId) clearInterval(this.pollId);
    if (this.pollTimeout) clearTimeout(this.pollTimeout);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.prevButton && this.prevButton.removeEventListener('click', this.handlePrev);
    this.nextButton && this.nextButton.removeEventListener('click', this.handleNext);
    window.removeEventListener('resize', this.handleResize);
    if (this.embla) {
      this.embla.off('select', this.update);
      this.embla.off('reInit', this.update);
    }
  }

  #embla() {
    return this.moast && this.moast._carousel;
  }

  // Moast hydrates its Embla instance asynchronously, and only once the carousel is
  // scrolled into view. Poll patiently rather than giving up after a few frames.
  #whenReady() {
    this.pollId = setInterval(() => {
      const embla = this.#embla();
      if (embla && typeof embla.scrollSnapList === 'function') {
        clearInterval(this.pollId);
        this.pollId = null;
        this.#sync();
      }
    }, 200);

    // Safety valve: stop polling after 60s. If it never hydrated, stay hidden.
    this.pollTimeout = setTimeout(() => {
      if (this.pollId) {
        clearInterval(this.pollId);
        this.pollId = null;
      }
    }, 60000);
  }

  // (Re)subscribe to the current Embla instance (the app may recreate it on
  // reInit / resize) and refresh the UI.
  #sync() {
    const embla = this.#embla();
    if (embla && embla !== this.embla) {
      if (this.embla) {
        this.embla.off('select', this.update);
        this.embla.off('reInit', this.update);
      }
      this.embla = embla;
      this.embla.on('select', this.update);
      this.embla.on('reInit', this.update);
    }
    // Moast doesn't reliably re-measure Embla after the layout settles or on
    // resize, leaving stale snap counts — e.g. at widths where every slide fits it
    // still reports multiple snaps, so the footer shows dead arrows. Forcing a
    // re-init makes the scroll state (snap count / canScroll) accurate for the
    // current width, so the footer hides when there's nothing to scroll and its
    // arrows work when there is. reInit fires 'reInit'/'select' (handled by
    // update, not #sync), so there is no loop.
    if (this.embla && typeof this.embla.reInit === 'function') {
      try {
        this.embla.reInit();
      } catch (error) {
        /* ignore — fall back to whatever state Embla is in */
      }
    }
    this.update();
  }

  // Shadow-DOM overrides for the Moast carousel. Its internals (and the app's
  // `custom_css` setting) can't be reached by theme CSS, so we inject a scoped
  // <style> into the (open) shadow root directly. Idempotent.
  //   1. Hide the app's native pagination + arrows (replaced by our footer).
  //   2. Left-align the slides: Moast sets `.slides{justify-content:center}`
  //      (its `alignment` setting), which centers the row when there are too few
  //      slides to fill the track; flex-start keeps them left-aligned. No effect
  //      once the track overflows (Embla drives it by transform).
  #hideNativeControls() {
    const root = this.moast && this.moast.shadowRoot;
    if (!root || root.getElementById('moast-nav-overrides')) return;
    const style = document.createElement('style');
    style.id = 'moast-nav-overrides';
    style.textContent =
      '.pagination,.carousel__prev,.carousel__next{display:none !important;}' +
      '.slides{justify-content:flex-start !important;}';
    root.appendChild(style);
  }

  // Navigation must go through the component (it owns scroll position in "cards"
  // mode). Fall back to the Embla API for other modes / future app versions.
  #navigate(direction) {
    const method = direction === 'previous' ? '_scrollToPrevious' : '_scrollToNext';
    if (this.moast && typeof this.moast[method] === 'function') {
      try {
        this.moast[method]();
        return;
      } catch (error) {
        /* fall through to Embla */
      }
    }
    const embla = this.embla || this.#embla();
    if (embla) {
      direction === 'previous' ? embla.scrollPrev() : embla.scrollNext();
    }
  }

  update() {
    // Re-assert on every tick so a Moast re-render can't leave its native
    // controls visible.
    this.#hideNativeControls();

    const embla = this.embla;
    if (!embla) return;

    // Only show the footer when there is room to scroll, mirroring the native
    // carousels and Moast's own arrows. Embla reports both false (and a single
    // snap) once every slide fits in view, so this stays correct across widths.
    const scrollable = embla.canScrollPrev() || embla.canScrollNext();
    this.hidden = !scrollable;
    if (!scrollable) return;

    // Counter in the native carousel-nav format ("01 — 07"). Moast snaps per
    // slide, so we report the snap position / snap count.
    const total = embla.scrollSnapList().length || 1;
    const current = Math.min(total, (embla.selectedScrollSnap() || 0) + 1);
    const pad = (value) => String(value).padStart(2, '0');
    if (this.counter) this.counter.textContent = `${pad(current)} — ${pad(total)}`;

    // Dim the arrows at the ends, like the native carousel-nav. (Moast runs with
    // loop enabled, so both stay enabled while scrollable — but this keeps parity
    // if looping is ever turned off.)
    if (this.prevButton) this.prevButton.disabled = !embla.canScrollPrev();
    if (this.nextButton) this.nextButton.disabled = !embla.canScrollNext();
  }
}

if (!customElements.get('moast-carousel-nav')) {
  customElements.define('moast-carousel-nav', MoastCarouselNav);
}

// --- Bootstrap: add a footer to every Moast carousel, wherever it appears ------

// Insert a footer (cloned from the template) as a sibling right after the given
// carousel's app block, unless its section already has one.
function insertMoastNav(carousel) {
  const section = carousel.closest('.shopify-section') || document;
  if (section.querySelector('moast-carousel-nav')) return;

  const template = document.getElementById(TEMPLATE_ID);
  if (!template || !template.content.firstElementChild) return;

  const appBlock =
    carousel.closest('.shopify-app-block') ||
    carousel.closest('[id*="moast_carousel"]') ||
    carousel.parentElement;
  if (!appBlock || !appBlock.parentElement) return;

  const nav = template.content.firstElementChild.cloneNode(true);
  appBlock.insertAdjacentElement('afterend', nav);
}

function scanForMoastCarousels() {
  for (const carousel of document.querySelectorAll('moast-carousel')) {
    insertMoastNav(carousel);
  }
}

function initMoastNav() {
  scanForMoastCarousels();

  // Moast can inject its carousel late (lazy hydration) or a section can be
  // re-rendered (e.g. PDP variant change), so keep watching — debounced.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scanForMoastCarousels();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMoastNav);
} else {
  initMoastNav();
}
